/**
 * Seasons: the saved copy first, ESPN behind it.
 *
 * Same read-through shape as `playerService`, for the same reasons, over
 * the `season` namespace declared in `cachePolicy.js`.
 *
 * WHY THIS IS THE PART WORTH SAVING
 * ---------------------------------
 * A schedule entry is a copy of something that will be re-fetched within
 * hours. A season entry is different in kind: once a season has finished,
 * its week structure and its dates never change again. The 2015 season had
 * seventeen regular weeks and ended on 2016-02-10, and it will still have
 * had seventeen regular weeks in ten years. So the entry written the first
 * time somebody opens 2015 is the right answer for every reader after
 * them, on every device, and that is precisely the durable, season-scoped
 * store this feature was asked for.
 *
 * The storage layer still refuses anything older than
 * `CACHE_HARD_MAX_AGE_MS`, which is a fortnight, so in practice an archived
 * season is re-confirmed against ESPN every couple of weeks rather than
 * trusted forever. That ceiling is deliberate and this module does not try
 * to talk its way around it: see the trust model in
 * `docs/security/cache-trust-model.md` for why a client-writable cache is
 * never allowed to be the last word.
 *
 * NOTHING HERE IS ON THE CRITICAL PATH
 * ------------------------------------
 * The Games page paints its schedule without ever waiting for a season.
 * Every function below can fail, and the only consequence of failure is
 * that the season picker does not appear. That is the whole design: the
 * app with a broken season endpoint behaves exactly like the app before
 * seasons existed.
 */

import { getBackend } from "../backend";
import espnProvider from "../sportsData/espnProvider";
import espnSeasonProvider, {
  REGULAR_SEASON_TYPE_ID,
  SEASON_FLOOR_YEAR,
} from "../sportsData/espnSeasonProvider";

/** The namespace this service owns. Declared in `cachePolicy.js`. */
const NAMESPACE = "season";

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * How long the catalogue is good for.
 *
 * Six hours because the one thing on it that moves is which season is
 * current, and that changes twice a year at a boundary nobody is watching
 * the clock for. Six hours means a rollover is picked up the same day
 * without asking a reference endpoint every time somebody opens a page.
 */
export const CATALOGUE_TTL_MS = 6 * HOUR;

/**
 * How long one season's week structure is good for.
 *
 * A week later, because for a finished season the honest answer is
 * "forever" and for the season being played it is "until the calendar is
 * republished", which happens between seasons rather than during one. The
 * storage layer's fortnight ceiling is the real limit either way.
 */
export const WEEKS_TTL_MS = 7 * DAY;

export const CATALOGUE_CACHE_KEY = "nfl-catalogue";
export const weeksCacheKey = (year) => `nfl-weeks-${year}`;

export { REGULAR_SEASON_TYPE_ID, SEASON_FLOOR_YEAR };

/* --------------------------------------------------------- cache */

/**
 * The backend, or null when there isn't one yet.
 *
 * `getBackend` throws while the Firebase chunk is still loading. For a
 * cache that is a miss, not an error.
 */
function backendOrNull() {
  try {
    return getBackend();
  } catch {
    return null;
  }
}

async function readCached(key) {
  const backend = backendOrNull();
  if (!backend) return null;

  try {
    return await backend.readCache(NAMESPACE, key);
  } catch {
    return null;
  }
}

async function writeThrough(key, value, fetchedAt) {
  const backend = backendOrNull();
  if (!backend) return;

  try {
    await backend.writeCache(NAMESPACE, key, value, {
      provider: espnSeasonProvider.id,
      fetchedAt,
    });
  } catch {
    // Signed out, offline, or over the size cap. All survivable: the next
    // visit pays for a live fetch again.
  }
}

const isFresh = (entry, ttlMs, now) =>
  entry != null &&
  Number.isFinite(entry.fetchedAt) &&
  now - entry.fetchedAt < ttlMs;

/* ----------------------------------------------------- catalogue */

/**
 * @typedef {Object} SeasonCatalogue
 * @property {number[]} years       every selectable season, newest first
 * @property {{year: number, displayName: string, startDate: string|null,
 *   endDate: string|null}} current the season being played now
 * @property {number} floorYear     the oldest season offered, and why
 */

/**
 * Every selectable season, and which one is being played.
 *
 * @param {object} [options]
 * @param {boolean} [options.force] ignore a fresh saved copy
 * @param {number} [options.now]
 * @returns {Promise<{catalogue: SeasonCatalogue, fetchedAt: number,
 *   origin: "cache"|"network"}>}
 *
 * Rejects when there is nothing to offer at all. A caller is expected to
 * treat that as "no picker", never as "no schedule".
 */
export async function loadSeasonCatalogue({ force = false, now = Date.now() } = {}) {
  const cached = await readCached(CATALOGUE_CACHE_KEY);

  if (!force && isFresh(cached, CATALOGUE_TTL_MS, now) && cached.value?.current) {
    return { catalogue: cached.value, fetchedAt: cached.fetchedAt, origin: "cache" };
  }

  try {
    const catalogue = await espnSeasonProvider.fetchSeasonCatalogue();
    await writeThrough(CATALOGUE_CACHE_KEY, catalogue, now);
    return { catalogue, fetchedAt: now, origin: "network" };
  } catch (error) {
    // A saved copy past its TTL still names the right seasons: the list
    // only grows, and a season that was current six hours ago is still the
    // one being played. Better than no picker.
    if (cached?.value?.current) {
      return { catalogue: cached.value, fetchedAt: cached.fetchedAt, origin: "cache" };
    }
    throw error;
  }
}

/* --------------------------------------------------------- weeks */

/**
 * One season's phases and weeks, with ESPN's own names on them.
 *
 * @param {number} year
 * @param {object} [options]
 * @param {number} [options.seasonType] which week to ask at, so the request
 *   can share the response the schedule is already fetching
 * @param {number} [options.week]
 * @returns {Promise<{weeks: {year: number, phases: object[],
 *   span: object|null}, fetchedAt: number, origin: "cache"|"network"}>}
 */
export async function loadSeasonWeeks(
  year,
  { seasonType = REGULAR_SEASON_TYPE_ID, week = 1, force = false, now = Date.now() } = {}
) {
  const key = weeksCacheKey(year);
  const cached = await readCached(key);

  if (!force && isFresh(cached, WEEKS_TTL_MS, now) && cached.value?.phases?.length) {
    return { weeks: cached.value, fetchedAt: cached.fetchedAt, origin: "cache" };
  }

  try {
    const weeks = await espnProvider.getSeasonWeeks({ year, seasonType, week, now });
    await writeThrough(key, weeks, now);
    return { weeks, fetchedAt: now, origin: "network" };
  } catch (error) {
    if (cached?.value?.phases?.length) {
      return { weeks: cached.value, fetchedAt: cached.fetchedAt, origin: "cache" };
    }
    throw error;
  }
}

/* -------------------------------------------------------- labels */

const MONTH_YEAR = { month: "short", year: "numeric" };

/**
 * "Aug 2026 to Feb 2027", from ESPN's own dates.
 *
 * This string is the whole answer to the naming trap. The season ESPN and
 * the NFL both call 2026 is the one somebody watching in January 2027 will
 * call "this year's", and its Super Bowl is in February 2027. Printing the
 * span next to the year means nobody has to know that.
 */
export function seasonSpanLabel(span) {
  const start = Date.parse(span?.startDate ?? "");
  const end = Date.parse(span?.endDate ?? "");
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;

  const format = (value) =>
    new Date(value).toLocaleDateString("en-GB", MONTH_YEAR);

  return `${format(start)} to ${format(end)}`;
}

/** "2026 season". The year is ESPN's, and so is the word. */
export const seasonLabel = (year) => `${year} season`;

/** The phase a week belongs to, and the week itself, or null. */
export function findWeek(phases, seasonType, week) {
  const phase = (phases ?? []).find(
    (entry) => entry.seasonTypeId === Number(seasonType)
  );
  if (!phase) return null;

  const found = phase.weeks.find((entry) => entry.number === Number(week));
  return found ? { phase, week: found } : null;
}

/**
 * "Wild Card" reads as a round rather than a week, and "Week 1" reads as
 * nothing at all without its phase, so a heading needs both unless the
 * week's own name already carries the phase in it.
 */
export function weekHeading(phases, seasonType, week) {
  const found = findWeek(phases, seasonType, week);
  if (!found) return null;

  const { phase, week: entry } = found;
  return entry.label.toLowerCase().includes(phase.label.toLowerCase())
    ? entry.label
    : `${phase.label}: ${entry.label}`;
}

/**
 * The first week worth opening a past season on.
 *
 * The regular season if the calendar has one, because that is what a
 * person means by "the 2015 season", and otherwise whatever the first
 * phase is. Never hard-coded to a type id that a given season might not
 * publish.
 */
export function defaultWeekFor(phases) {
  const list = phases ?? [];
  if (list.length === 0) return null;

  const regular =
    list.find((phase) => phase.seasonTypeId === REGULAR_SEASON_TYPE_ID) ?? list[0];

  const week = regular.weeks[0];
  return week ? { seasonType: regular.seasonTypeId, week: week.number } : null;
}
